<script setup lang="ts">
import {computed, ref, watch} from 'vue'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import FormInputText from '@/components/inputs/FormInputText.vue'
import FormCalendar from '@/components/inputs/FormCalendar.vue'
import { Icon } from '@iconify/vue'
import {useToast} from 'primevue/usetoast'

const toast = useToast()
const loading = ref(false)
const props = defineProps<{
  visible: boolean
  packingListNumber: string | null
  actionStatus: string
  confirmLabel: string
  enteredBy: string
}>()

const dialogueVisible = ref(props.visible)
const dispatchDate = ref<Date | null>(null)
const deliveryDate = ref<Date | null>(null)
const enteredByValue = ref('')

watch(() => props.actionStatus, (newValue) => {
  if (newValue === 'Dispatched') {
    dispatchDate.value = null
  } else if (newValue === 'Delivered') {
    deliveryDate.value = null
  }
})

watch(() => props.visible, (newValue) => {
  dialogueVisible.value = newValue
})

watch(() => props.enteredBy, (newValue) => {
  enteredByValue.value = newValue
})

const emit = defineEmits(['update:visible', 'confirm'])

function closeDialog() {
  dialogueVisible.value = false
  emit('update:visible', false)
}

const showToast = (type: 'success' | 'error', title: string, message: string) => {
  toast.add({
    severity: type,
    summary: title,
    detail: message,
    life: 5000
  })
}

const onConfirm = () => {
  const enteredBy = enteredByValue.value.trim()
  const dateValue = props.actionStatus === 'Dispatched' ? dispatchDate.value : deliveryDate.value

  if (!dateValue) {
    showToast('error', 'Validation Error', `${props.actionStatus} Date is required.`)
    return
  }

  if (!enteredBy) {
    showToast('error', 'Validation Error', '"Entered By" is required.')
    return
  }

  loading.value = true

  emit('confirm', {
    dispatchDate: dispatchDate.value,
    deliveryDate: deliveryDate.value,
    enteredBy
  })

  showToast('success', 'Success', `Packing list marked as ${props.actionStatus.toLowerCase()}.`)
  closeDialog()
}

const dateModel = computed<Date | null>({
  get: () => (props.actionStatus === 'Dispatched' ? dispatchDate.value : deliveryDate.value),
  set: (value) => {
    if (props.actionStatus === 'Dispatched') {
      dispatchDate.value = value
    } else if (props.actionStatus === 'Delivered') {
      deliveryDate.value = value
    }
  }
})
</script>

<template>
  <Dialog
    v-model:visible="dialogueVisible"
    modal
    :draggable="false"
    class="max-w-xl"
    :closable="false"
  >
    <template #header>
      <div class="flex flex-col gap-3 items-center">
        <h3 class="text-2xl">Packing List {{ packingListNumber }}</h3>
        <p class="text-neutral-500 text-sm text-center">
          This action will change the status of the packing list to
          <strong>{{ actionStatus }}</strong>
        </p>
      </div>
    </template>
    <div class="flex flex-col gap-3 mb-8">
      <FormCalendar
        :label="`${actionStatus} Date:`"
        :inputName="`${actionStatus.toLowerCase()}Date`"
        :id="`${actionStatus.toLowerCase()}Date`"
        placeholder="Enter date"
        v-model="dateModel"
      />
      <FormInputText
        label="Entered By:"
        inputName="lastEditedBy"
        id="lastEditedBy"
        v-model="enteredByValue"
      />
    </div>
    <template #footer>
      <Button @click="closeDialog" class="min-w-48" severity="secondary">
        <div class="flex justify-center items-center gap-2">
          <p>Cancel</p>
          <Icon
            icon="material-symbols:cancel-presentation-rounded"
            class="text-2xl ml-3 order-1 text-gray-500"
          />
        </div>
      </Button>
      <Button :label="confirmLabel" @click="onConfirm" class="min-w-48" />
    </template>
  </Dialog>
</template>
